import { LinkError } from '@apollo/client/errors'
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnInit,
} from '@angular/core'
import { ReactiveFormsModule, FormControl } from '@angular/forms'
import { MatButtonModule } from '@angular/material/button'
import { MatIconModule } from '@angular/material/icon'
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner'

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import {
  debounceTime,
  distinctUntilChanged,
  finalize,
  from,
  switchMap,
} from 'rxjs'

import { KpiEmployeeNumberImportStatus } from '../../../../_generated/graphql'
import { FileUploaderComponent } from '../../../components/file-uploader/file-uploader.component'
import {
  DialogResult,
  MessageDialogService,
} from '../../../components/message-dialog/message-dialog.service'
import {
  KpiDashboardDefinition,
  KpiDashboardService,
  KpiEmployeeNumberImportResultView,
  KpiEmployeeNumberMappingView,
} from '../../../services/kpi-dashboard/kpi-dashboard.service'
import { downloadBlob } from '../../../util/blob-download'
import { captureException } from '../../../util/captureException'
import {
  buildKpiTemplateCsvBlob,
  formatFileSize,
  validateKpiImportFile,
} from '../../../util/file/kpi-import'
import { readFileAsBase64 } from '../../../util/file/reader'
import { normalizeSearchText } from '../../../util/search-text'
import {
  applyEmployeeNumberDeletion,
  filterKpiEmployeeNumberMappings,
  formatKpiEmployeeNumberCount,
  getEmployeeNumberFileErrorMessage,
  isEmployeeNumberUnset,
} from './kpi-employee-number-settings.logic'

@UntilDestroy()
@Component({
  selector: 'app-kpi-employee-number-settings',
  standalone: true,
  imports: [
    FileUploaderComponent,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    ReactiveFormsModule,
  ],
  templateUrl: './kpi-employee-number-settings.component.html',
  styleUrl: './kpi-employee-number-settings.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class KpiEmployeeNumberSettingsComponent implements OnInit {
  @Input({ required: true }) dashboard!: KpiDashboardDefinition

  readonly acceptFileExtensions = ['csv']
  readonly formatFileSize = formatFileSize
  readonly importStatus = KpiEmployeeNumberImportStatus
  readonly searchControl = new FormControl('', { nonNullable: true })

  mappings: KpiEmployeeNumberMappingView[] = []
  filteredMappings: KpiEmployeeNumberMappingView[] = []
  countText = '全 0 名（未設定 0 名）'
  appliedSearchQuery = ''
  selectedFile: File | null = null
  previewResult: KpiEmployeeNumberImportResultView | null = null
  confirmedResult: KpiEmployeeNumberImportResultView | null = null
  deletingUserId: string | null = null
  isLoading = true
  isPreviewing = false
  isImporting = false
  isRefreshing = false
  isDownloadingTemplate = false
  loadErrorMessage = ''
  importErrorMessage = ''
  deleteErrorMessage = ''
  importOperationMessage = ''
  mappingOperationMessage = ''

  private fileBase64: string | null = null

  constructor(
    private kpiDashboardService: KpiDashboardService,
    private messageDialogService: MessageDialogService,
    private changeDetectorRef: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.searchControl.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged(), untilDestroyed(this))
      .subscribe((searchQuery) => {
        this.appliedSearchQuery = searchQuery
        this.recalculateFilteredMappings()
        this.changeDetectorRef.markForCheck()
      })
    this.loadMappings()
  }

  get isSearching(): boolean {
    return normalizeSearchText(this.appliedSearchQuery) !== ''
  }

  get isBusy(): boolean {
    return (
      this.isPreviewing ||
      this.isImporting ||
      this.isRefreshing ||
      this.isDownloadingTemplate ||
      this.deletingUserId != null
    )
  }

  get canConfirmImport(): boolean {
    return (
      this.previewResult != null &&
      this.fileBase64 != null &&
      this.confirmedResult == null &&
      this.previewResult.fileErrors.length === 0 &&
      !this.isBusy
    )
  }

  retry(): void {
    this.loadMappings()
  }

  onFilesSelected(files: File[]): void {
    if (files.length === 0) {
      return
    }
    if (this.isBusy) {
      this.showImportError(
        '処理中はファイルを変更できません。',
        'ファイル変更エラー',
      )
      return
    }

    const file = files[0]
    const validationError = validateKpiImportFile(
      file,
      this.acceptFileExtensions,
    )
    if (validationError != null) {
      this.selectedFile = null
      this.resetImportResults()
      this.showImportError(validationError.message, validationError.title)
      return
    }

    this.selectedFile = file
    this.resetImportResults()
    this.preview()
  }

  preview(): void {
    if (this.selectedFile == null || this.isBusy) {
      return
    }

    this.isPreviewing = true
    this.previewResult = null
    this.confirmedResult = null
    this.importErrorMessage = ''
    this.importOperationMessage = ''
    this.changeDetectorRef.markForCheck()

    from(readFileAsBase64(this.selectedFile))
      .pipe(
        switchMap((fileBase64) => {
          this.fileBase64 = fileBase64
          return this.kpiDashboardService.importEmployeeNumbers(
            this.dashboard.id,
            fileBase64,
            true,
          )
        }),
        finalize(() => {
          this.isPreviewing = false
          this.changeDetectorRef.markForCheck()
        }),
        untilDestroyed(this),
      )
      .subscribe({
        next: (result) => {
          this.previewResult = result
          const fileErrorMessage = getEmployeeNumberFileErrorMessage(result)
          if (fileErrorMessage != null) {
            this.importOperationMessage = ''
            this.showImportError(fileErrorMessage, 'ファイルエラー')
            return
          }
          this.importOperationMessage = `プレビューを表示しました。新規 ${result.newCount} 件・更新 ${result.updatedCount} 件（変更なし ${result.unchangedCount} 件・エラー ${result.errorCount} 件）です。`
        },
        error: (error: unknown) => {
          this.fileBase64 = null
          this.handleImportError(error, '社員番号のプレビューに失敗しました。')
        },
      })
  }

  confirmImport(): void {
    if (!this.canConfirmImport || this.fileBase64 == null) {
      return
    }

    this.isImporting = true
    this.importErrorMessage = ''
    this.importOperationMessage = ''
    this.changeDetectorRef.markForCheck()
    this.kpiDashboardService
      .importEmployeeNumbers(this.dashboard.id, this.fileBase64, false)
      .pipe(
        finalize(() => {
          this.isImporting = false
          this.changeDetectorRef.markForCheck()
        }),
        untilDestroyed(this),
      )
      .subscribe({
        next: (result) => {
          this.confirmedResult = result
          const fileErrorMessage = getEmployeeNumberFileErrorMessage(result)
          if (fileErrorMessage != null) {
            this.importOperationMessage = ''
            this.showImportError(fileErrorMessage, 'ファイルエラー')
            return
          }
          const importCompletedMessage = `新規 ${result.newCount} 件・更新 ${result.updatedCount} 件を取り込みました（変更なし ${result.unchangedCount} 件・エラー ${result.errorCount} 件）。`
          this.importOperationMessage = importCompletedMessage
          this.loadErrorMessage = ''
          // 取込完了後はプレビューを閉じ、対照一覧で結果を確認できるようにする
          this.previewResult = null
          this.selectedFile = null
          this.fileBase64 = null
          this.refreshMappings(importCompletedMessage)
        },
        error: (error: unknown) => {
          this.handleImportError(error, '社員番号の取り込みに失敗しました。')
        },
      })
  }

  downloadTemplate(): void {
    if (this.isBusy) {
      return
    }

    this.isDownloadingTemplate = true
    this.changeDetectorRef.markForCheck()
    this.kpiDashboardService
      .employeeNumberImportTemplate(this.dashboard.id)
      .pipe(
        finalize(() => {
          this.isDownloadingTemplate = false
          this.changeDetectorRef.markForCheck()
        }),
        untilDestroyed(this),
      )
      .subscribe({
        next: (template) => {
          const blob = buildKpiTemplateCsvBlob(template)
          downloadBlob(blob, 'kpi_employee_numbers_template.csv')
        },
        error: (error: unknown) => {
          this.handleImportError(
            error,
            '社員番号テンプレートの取得に失敗しました。',
          )
        },
      })
  }

  requestDelete(mapping: KpiEmployeeNumberMappingView): void {
    const employeeNumber = mapping.employeeNumber
    if (this.isBusy || isEmployeeNumberUnset(mapping)) {
      return
    }

    this.messageDialogService
      .showConfirm(
        `「${mapping.userName}」の社員番号「${employeeNumber}」を削除します。削除した社員番号は元に戻せません。この対応は org 全体で共有されており、削除は全ての個人支援ボードの突合に影響します。`,
        {
          title: '社員番号削除の確認',
          primaryButtonText: '削除する',
          secondaryButtonText: 'キャンセル',
        },
      )
      .pipe(untilDestroyed(this))
      .subscribe((result) => {
        if (result === DialogResult.PrimaryButtonClicked) {
          this.deleteMapping(mapping)
        }
      })
  }

  isUnset(mapping: KpiEmployeeNumberMappingView): boolean {
    return isEmployeeNumberUnset(mapping)
  }

  statusLabel(status: KpiEmployeeNumberImportStatus): string {
    switch (status) {
      case KpiEmployeeNumberImportStatus.New:
        return '新規'
      case KpiEmployeeNumberImportStatus.Updated:
        return '更新'
      case KpiEmployeeNumberImportStatus.Unchanged:
        return '変更なし'
      case KpiEmployeeNumberImportStatus.Error:
        return 'エラー'
    }
  }

  private loadMappings(): void {
    this.isLoading = true
    this.loadErrorMessage = ''
    this.changeDetectorRef.markForCheck()
    this.kpiDashboardService
      .employeeNumberMappings(this.dashboard.id)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (mappings) => {
          this.mappings = [...mappings]
          this.isLoading = false
          this.recalculateFilteredMappings()
          this.changeDetectorRef.markForCheck()
        },
        error: (error: unknown) => {
          captureException(error)
          this.isLoading = false
          this.loadErrorMessage = '社員番号の対照一覧を読み込めませんでした。'
          this.changeDetectorRef.markForCheck()
        },
      })
  }

  /** 現在の一覧 DOM を維持したまま、取込後の対照一覧を更新する。 */
  private refreshMappings(importCompletedMessage: string): void {
    this.isRefreshing = true
    this.changeDetectorRef.markForCheck()
    this.kpiDashboardService
      .employeeNumberMappings(this.dashboard.id)
      .pipe(
        finalize(() => {
          this.isRefreshing = false
          this.changeDetectorRef.markForCheck()
        }),
        untilDestroyed(this),
      )
      .subscribe({
        next: (mappings) => {
          this.mappings = [...mappings]
          this.recalculateFilteredMappings()
          this.importOperationMessage = `${importCompletedMessage}対照一覧を更新しました。`
          this.changeDetectorRef.markForCheck()
        },
        error: (error: unknown) => {
          captureException(error)
        },
      })
  }

  private deleteMapping(mapping: KpiEmployeeNumberMappingView): void {
    this.deletingUserId = mapping.userId
    this.deleteErrorMessage = ''
    this.mappingOperationMessage = ''
    this.changeDetectorRef.markForCheck()
    this.kpiDashboardService
      .deleteEmployeeNumber(this.dashboard.id, mapping.userId)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: () => {
          this.deletingUserId = null
          this.mappings = applyEmployeeNumberDeletion(this.mappings, mapping)
          this.recalculateFilteredMappings()
          this.mappingOperationMessage = `${mapping.userName}の社員番号を削除しました。`
          this.changeDetectorRef.markForCheck()
        },
        error: (error: unknown) => this.handleDeleteError(error),
      })
  }

  private handleDeleteError(error: unknown): void {
    captureException(error)
    this.deletingUserId = null
    this.deleteErrorMessage =
      '社員番号を削除できませんでした。時間を置いて再度お試しください。'
    this.changeDetectorRef.markForCheck()
  }

  private handleImportError(error: unknown, fallbackMessage: string): void {
    captureException(error)
    // リンク由来（通信断・非 2xx 応答）のエラーは英語の技術的な文言なので定型文にする
    const message =
      error instanceof Error && !LinkError.is(error) && error.message
        ? error.message
        : fallbackMessage
    this.showImportError(message, '処理エラー')
  }

  private showImportError(message: string, title: string): void {
    this.importErrorMessage = message
    this.messageDialogService.showError(message, { title })
    this.changeDetectorRef.markForCheck()
  }

  private resetImportResults(): void {
    this.fileBase64 = null
    this.previewResult = null
    this.confirmedResult = null
    this.importErrorMessage = ''
    this.importOperationMessage = ''
  }

  private recalculateFilteredMappings(): void {
    this.filteredMappings = filterKpiEmployeeNumberMappings(
      this.mappings,
      this.appliedSearchQuery,
    )
    const displayedUnsetCount = this.filteredMappings.filter(
      isEmployeeNumberUnset,
    ).length
    this.countText = formatKpiEmployeeNumberCount(
      this.filteredMappings.length,
      displayedUnsetCount,
      this.appliedSearchQuery,
    )
  }
}
